import React from 'react';
import { Link } from 'react-router-dom';

export const PrivacyPolicy = () => {
  const policyData = {
    lastUpdated: "September 6, 2024",
    sections: [
      {
        title: "1. Introduction",
        content: `Welcome to Namasvi Nexus ("we," "our," "us"). We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you use our mobile application (the "App"), web portals, and related services across the Namasvi ecosystem.`
      },
      {
        title: "2. Information We Collect",
        content: `a. Personal Information:
• Registration Data: Name, email address, phone number, location, and other contact details provided during account creation.
• Social Media Profiles: Public profile data, username, followers count, and engagement metrics via Meta Profile Analytics (Instagram / Facebook).

b. Usage Data:
• App Interactions: Event registrations, participation voting, wallet transactions, and automated workflows.
• Device Information: Device type, operating system version, IP address, browser type, and technical diagnostic identifiers.

c. Content & Media:
• User-Generated Content: Photos, videos, posters, reels, and captions uploaded or submitted for event participation or sponsor collaborations.`
      },
      {
        title: "3. How We Use Your Information",
        content: `• Event Management: Process and organize digital talent contests, verify participant submissions, and manage leaderboard rankings.
• Transparent Voting: Calculate and verify public engagement votes (likes, comments, shares, saves) to determine contest rankers.
• Brand & Sponsor Collaborations: Connect creators with verified brand sponsors for promotional campaigns and reward distribution.
• Wallet & Rewards: Track Namasvi coins, referral balances, top-ups, and gift hamper allotments.
• Communication & Support: Deliver critical account updates, contest announcements, security notifications, and customer assistance.`
      },
      {
        title: "4. Sharing Your Information",
        content: `We do not sell, rent, or trade your personal information. We may share information under the following limited circumstances:
• Service Providers: Trusted infrastructure, cloud hosting, and technical vendors supporting our platform operations.
• Event Sponsors: Only necessary participant profile data required to fulfill co-branded prizes, vouchers, or collaboration agreements.
• Legal Compliance: When required by law, subpoena, regulatory authority, or to protect the vital rights and security of our community.`
      },
      {
        title: "5. Permissions Required from Meta Platforms",
        content: `To enable features such as Meta Profile Analytics, Instagram business connectivity, and authentic engagement voting:
• Public Profile & Insights: To display your verified social presence and analyze post reach.
• Content Publishing: To facilitate scheduled posts or collaborative reels upon your explicit in-app action.
• Meta Policy Adherence: All data access strictly adheres to the Meta Developer Platform Terms and Data Policy.`
      },
      {
        title: "6. Your Rights, Choices & Data Deletion",
        content: `• Access & Corrections: Review and edit your profile details at any time within your account settings.
• Account & Data Deletion: You have the right to request permanent deletion of your account and associated data by emailing trishul@namasvinexus.com or submitting a request via in-app Help & Support.
• Communications Opt-Out: Unsubscribe from promotional notifications at any time while retaining critical transactional alerts.`
      },
      {
        title: "7. Data Security & Storage",
        content: `We employ industry-standard encryption protocols (TLS/SSL in transit and AES-256 at rest), firewall protections, and strict access controls across Google Cloud Platform and Firebase infrastructure to safeguard your information against unauthorized access or disclosure.`
      },
      {
        title: "8. Children's Privacy",
        content: `Namasvi Nexus is intended for individuals aged 18 and older (or minors participating with verified parental/guardian consent where permitted by specific cultural events). We do not knowingly collect personal information from children under 13.`
      },
      {
        title: "9. Policy Updates",
        content: `We may revise this Privacy Policy periodically to reflect platform enhancements or regulatory requirements. Any updates will be posted on this page with a revised "Last Updated" date.`
      },
      {
        title: "10. Contact Us",
        content: `If you have questions, feedback, or data privacy requests regarding this policy, please reach out to:
• Email: trishul@namasvinexus.com / namasvinexus@gmail.com
• Company: Namasvi Nexus Private Limited
• Website: https://namasvinexus.com`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans">
      {/* Top Standalone Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-black text-xl shadow-md shadow-blue-500/20">
              N
            </div>
            <div>
              <span className="text-xl font-extrabold tracking-tight text-slate-900">
                NAMASVI<span className="text-blue-600 font-bold">NEXUS</span>
              </span>
              <span className="hidden sm:inline-block ml-3 px-2.5 py-0.5 text-xs font-semibold bg-blue-50 text-blue-700 rounded-full border border-blue-100">
                Official Legal Portal
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-3 text-sm font-medium">
            <Link to="/privacypolicy" className="text-blue-600 font-bold border-b-2 border-blue-600 pb-1">Privacy</Link>
            <Link to="/termcondition" className="text-slate-600 hover:text-slate-900 transition-colors pb-1">Terms</Link>
            <Link to="/fairpolicy" className="text-slate-600 hover:text-slate-900 transition-colors pb-1">Fair Play</Link>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-10">
        <div className="bg-white rounded-3xl p-6 sm:p-12 shadow-sm border border-slate-200/80">
          <div className="border-b border-slate-100 pb-6 mb-8 text-center sm:text-left">
            <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-lg mb-3">
              Legal Documentation
            </span>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-2">
              Privacy Policy
            </h1>
            <p className="text-sm font-medium text-slate-500">
              Last Updated: <span className="text-slate-700 font-semibold">{policyData.lastUpdated}</span>
            </p>
          </div>

          <div className="space-y-8">
            {policyData.sections.map((section, index) => (
              <div key={index} className="space-y-2">
                <h2 className="text-xl font-bold text-slate-900">
                  {section.title}
                </h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Contact Card Box */}
          <div className="mt-12 p-6 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-base">Have privacy inquiries or data requests?</h3>
              <p className="text-sm text-slate-600 mt-0.5">Contact our Data Protection & Compliance desk anytime.</p>
            </div>
            <a
              href="mailto:trishul@namasvinexus.com"
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm inline-block whitespace-nowrap"
            >
              Email Support
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 text-center text-xs text-slate-500">
        <p>© {new Date().getFullYear()} Namasvi Nexus Private Limited. All rights reserved.</p>
      </footer>
    </div>
  );
};
